package com.meistercharts.charts.pv.roofPlanning

import com.meistercharts.algorithms.painter.FillAndStrokeStyle
import com.meistercharts.canvas.interaction.ElementVisualState
import com.meistercharts.color.Color
import com.meistercharts.color.RgbaColor
import com.meistercharts.color.withAlpha
import it.neckar.open.kotlin.lang.asProvider

/**
 * How a [ModuleArea] is painted in one [ElementVisualState].
 */
data class ModuleAreaStyle(
  /**
   * If set to true the location and size are shown
   */
  val showLocationAndSize: Boolean,

  /**
   * Returns true if the delete icon is shown.
   * Attention: this is different from [showArmedDeleteIcon]
   */
  val showDeleteIcon: Boolean,

  val showArmedDeleteIcon: Boolean,

  val showRotateIcon: Boolean,

  val showArmedRotateIcon: Boolean,

  /**
   * The fill and stroke that is used for that state
   */
  val fillAndStroke: FillAndStrokeStyle,
) {
  companion object {
    /**
     * A module area the user is not interacting with is not painted at all - the roof below it shows through.
     */
    val Normal: ModuleAreaStyle = ModuleAreaStyle(
      showLocationAndSize = false,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      showRotateIcon = false,
      showArmedRotateIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.white.withAlpha(0.0), Color.white.withAlpha(0.0)),
    )

    val Hover: ModuleAreaStyle = ModuleAreaStyle(
      showLocationAndSize = true,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      showRotateIcon = false,
      showArmedRotateIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.web("#75b72600"), Color.web("#75b726")),
    )

    val Selected: ModuleAreaStyle = ModuleAreaStyle(
      showLocationAndSize = true,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      showRotateIcon = false,
      showArmedRotateIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.web("#75b7264D"), Color.web("#75b726")),
    )

    val SelectedHovering: ModuleAreaStyle = ModuleAreaStyle(
      showLocationAndSize = true,
      showDeleteIcon = true,
      showArmedDeleteIcon = false,
      showRotateIcon = true,
      showArmedRotateIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.web("#75b7267D"), Color.web("#75b726")),
    )

    val Dragging: ModuleAreaStyle = ModuleAreaStyle(
      showLocationAndSize = true,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      showRotateIcon = false,
      showArmedRotateIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.white.withAlpha(0.2), Color.white),
    )

    val DeleteArmed: ModuleAreaStyle = ModuleAreaStyle(
      showLocationAndSize = false,
      showDeleteIcon = false,
      showArmedDeleteIcon = true,
      showRotateIcon = false,
      showArmedRotateIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.red.withAlpha(0.3), Color.red),
    )

    val RotateArmed: ModuleAreaStyle = ModuleAreaStyle(
      showLocationAndSize = false,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      showRotateIcon = false,
      showArmedRotateIcon = true,
      fillAndStroke = FillAndStrokeStyle(Color.blue.withAlpha(0.3), Color.blue),
    )

    /**
     * The style a module area is painted with while the roof is only rendered, not planned.
     */
    val Rendering: ModuleAreaStyle = ModuleAreaStyle(
      showLocationAndSize = false,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      showRotateIcon = false,
      showArmedRotateIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.white.withAlpha(0.5), RgbaColor(86, 86, 86).asProvider()),
    )

    fun forVisualState(visualState: ElementVisualState): ModuleAreaStyle {
      return when (visualState) {
        ElementVisualState.Normal -> Normal
        ElementVisualState.Hover -> Hover
        ElementVisualState.Selected -> Selected
        ElementVisualState.SelectedHovering -> SelectedHovering
        ElementVisualState.Dragging -> Dragging
        ElementVisualState.DeleteArmed -> DeleteArmed
        ElementVisualState.RotateArmed -> RotateArmed
      }
    }
  }
}
